import { useState } from 'react'
import { PageContainer, ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Alert, Button, Calendar, DatePicker, Drawer, Space, Spin, Tag } from 'antd'
import dayjs from 'dayjs'
import { z } from 'zod'
import { customerSendPermissions } from '@super-mega/server/contracts/bill'
import { DRAWER_SIZE } from '@/const/drawer.ts'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { CalendarSendAction } from '@/features/customer-documents/index.ts'
import { customerSearchSchema } from '@/features/master-data/index.ts'
import { useMemberCreatorOptions, usePageTableNavigation, type DateRangeFormValue } from '@/hooks/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { createListQueryPersistence } from '@/utils/list-query-persistence.ts'
import { getCreatedAtSearch, getCreatedAtSortOrder, getDateRangeFormValue } from '@/utils/list-query.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'

const calendarSearchSchema = customerSearchSchema
  .pick({
    current: true,
    pageSize: true,
    id: true,
    keyword: true,
    creatorMemberId: true,
    createdFrom: true,
    createdTo: true,
    sortField: true,
    sortOrder: true
  })
  .extend({
    date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .refine((value) => dayjs(value).format('YYYY-MM-DD') === value)
      .optional()
      .catch(undefined)
  })
const persistence = createListQueryPersistence('super-mega:customer-management-calendar', calendarSearchSchema)
type Search = z.infer<typeof calendarSearchSchema>
type SearchForm = Pick<Search, 'id' | 'keyword' | 'creatorMemberId'> & { createdAtRange?: DateRangeFormValue }

export const Route = createFileRoute('/_authenticated/customer-management/calendar')({
  head: () => ({ meta: [{ title: '客户台历 · Super Mega' }] }),
  validateSearch: persistence.validateSearch,
  beforeLoad: (options) => persistence.restore(options),
  component: CustomerCalendarPage
})

function CustomerCalendarPage() {
  const trpc = useTRPC()
  const access = useAccess()
  const navigate = Route.useNavigate()
  const { currentSession } = Route.useRouteContext()
  const { search: query, navigateSearch } = usePersistedListQuery({
    persistence,
    raw: Route.useSearch(),
    accountId: currentSession.id,
    navigate
  })
  const [detailsOpen, setDetailsOpen] = useState(false)
  const selected = dayjs(query.date ?? dayjs().format('YYYY-MM-DD'))
  const summary = useQuery(trpc.customer.billingCalendar.queryOptions())
  const day = selected.date()
  const customers = useQuery({
    ...trpc.customer.list.queryOptions({ ...query, status: 'enabled', collectionBillGroupingDay: Math.min(day, 28) }),
    enabled: detailsOpen && day <= 28,
    placeholderData: (previous, previousQuery) => {
      const previousInput = previousQuery?.queryKey[1] as { input?: { collectionBillGroupingDay?: number } } | undefined
      if (previousInput?.input?.collectionBillGroupingDay === day) return keepPreviousData(previous)
      return undefined
    }
  })
  const creator = useMemberCreatorOptions('customer.read', query.creatorMemberId)
  const table = usePageTableNavigation<Search, SearchForm>({
    defaultSearch: calendarSearchSchema.parse({ date: query.date }),
    showTotal: (total) => `共 ${total} 个客户`,
    search: query,
    navigate: navigateSearch,
    getFormInitialValues: (search) => ({
      id: search.id,
      keyword: search.keyword,
      creatorMemberId: search.creatorMemberId,
      createdAtRange: getDateRangeFormValue(search.createdFrom, search.createdTo)
    }),
    getSearchFromForm: (values) => ({
      date: query.date,
      id: values.id,
      keyword: values.keyword?.trim() || undefined,
      creatorMemberId: values.creatorMemberId,
      ...getCreatedAtSearch(values.createdAtRange)
    })
  })
  const selectDate = (date: dayjs.Dayjs) => {
    void navigate({ search: { ...query, date: date.format('YYYY-MM-DD'), current: 1 } })
  }
  const unscheduled = (summary.data ?? [])
    .filter((item) => item.day === null)
    .reduce((sum, item) => sum + item.count, 0)
  type Customer = NonNullable<typeof customers.data>['data'][number]
  const columns: ProColumns<Customer>[] = [
    { dataIndex: 'keyword', title: '客户名称 / ID', hideInTable: true },
    {
      dataIndex: 'id',
      title: 'ID',
      valueType: 'digit',
      copyable: true,
      width: '7rem',
      fieldProps: { min: 1, precision: 0 }
    },
    {
      dataIndex: 'name',
      title: '客户名称',
      search: false,
      ellipsis: true,
      width: '20rem',
      render: (_, customer) => {
        if (!access.visit(`/customer-management/customers/${customer.id}/edit`)) return customer.name
        return (
          <Link
            to="/customer-management/customers/$customerId/edit"
            params={{ customerId: String(customer.id) }}
            search={{}}
          >
            {customer.name}
          </Link>
        )
      }
    },
    {
      dataIndex: 'autoCollectionBillGrouping',
      title: '归组方式',
      search: false,
      width: '9rem',
      render: (_, customer) => (
        <Tag color={customer.autoCollectionBillGrouping ? 'processing' : 'default'}>
          {customer.autoCollectionBillGrouping ? '自动生成' : '人工处理'}
        </Tag>
      )
    },
    {
      dataIndex: 'autoSendCollectionBillGroup',
      title: '自动发送配置',
      search: false,
      width: '10rem',
      render: (_, customer) => (customer.autoSendCollectionBillGroup ? '已开启（待接入）' : '关闭')
    },
    {
      dataIndex: 'wecomChatId',
      title: '企微群 ID',
      search: false,
      copyable: true,
      ellipsis: true,
      width: '16rem',
      renderText: (value: string | null) => value || '—'
    },
    {
      dataIndex: 'creatorMemberId',
      title: '创建人',
      hideInTable: true,
      valueType: 'select',
      fieldProps: { options: creator.options, loading: creator.isLoading, ...proRemoteSearch(creator.onSearch) }
    },
    {
      dataIndex: 'creator',
      title: '创建人',
      search: false,
      width: '9rem',
      render: (_, customer) => customer.creator.name
    },
    {
      dataIndex: 'createdAtRange',
      title: '创建时间',
      hideInTable: true,
      valueType: 'dateRange',
      fieldProps: { presets: RANGE_PRESETS }
    },
    {
      dataIndex: 'createdAt',
      title: '创建时间',
      search: false,
      valueType: 'dateTime',
      sorter: true,
      sortOrder: query.sortOrder === 'asc' ? 'ascend' : 'descend',
      width: '12rem'
    }
  ]
  return (
    <PageContainer
      title="客户台历"
      breadcrumbRender={false}
      content="按每月归组日展示在用客户的出账计划，点击日期在右侧查看当日计划出账客户。自动生成仍以起始月份和实际账单条件为准。"
    >
      {summary.isError ? (
        <Alert
          className="mb-4"
          type="error"
          showIcon
          title={getErrorMessage(summary.error, '台历加载失败')}
          action={<Button onClick={() => void summary.refetch()}>重试</Button>}
        />
      ) : null}
      {unscheduled > 0 ? (
        <Alert
          className="mb-4"
          type="warning"
          showIcon
          title={`另有 ${unscheduled} 个客户未设置归组日，暂未列入台历，请在客户档案补充设置。`}
        />
      ) : null}
      <div className="bg-surface mb-5 overflow-x-auto rounded-lg p-4">
        <Spin spinning={summary.isFetching}>
          <Calendar
            className="min-w-[44rem]"
            value={selected}
            mode="month"
            onSelect={(date, info) => {
              selectDate(date)
              if (info.source === 'date') setDetailsOpen(true)
            }}
            headerRender={() => (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <Space>
                  <Button onClick={() => selectDate(selected.subtract(1, 'month'))}>上月</Button>
                  <DatePicker
                    picker="month"
                    allowClear={false}
                    value={selected}
                    onChange={(date) => {
                      if (date) selectDate(date.startOf('month'))
                    }}
                    aria-label="选择月份"
                  />
                  <Button onClick={() => selectDate(selected.add(1, 'month'))}>下月</Button>
                  <Button onClick={() => selectDate(dayjs())}>本月</Button>
                </Space>
                <span className="text-content-muted text-sm">点击日期查看全部客户</span>
              </div>
            )}
            cellRender={(date, info) => {
              if (info.type !== 'date') return info.originNode
              if (!date.isSame(selected, 'month')) return null
              const scheduled = summary.data?.find((item) => item.day === date.date())
              if (!scheduled) return null
              const remaining = scheduled.count - scheduled.customers.length
              return (
                <ul className="m-0 list-none p-0 text-left text-xs leading-5">
                  {scheduled.customers.map((customer) => (
                    <li key={customer.id} title={customer.name} className="truncate">
                      {customer.name}
                    </li>
                  ))}
                  {remaining > 0 && <li className="text-accent truncate">另有 {remaining} 位客户，点击查看</li>}
                </ul>
              )
            }}
          />
        </Spin>
      </div>
      <Drawer
        title={`${selected.format('YYYY 年 MM 月 DD 日')} · 计划出账客户`}
        size={DRAWER_SIZE}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        extra={
          <Space wrap>
            {access.all(customerSendPermissions) && (
              <CalendarSendAction
                date={selected.format('YYYY-MM-DD')}
                count={(summary.data ?? [])
                  .filter((item) => item.day === day)
                  .reduce((sum, item) => sum + item.count, 0)}
              />
            )}
            <Button
              onClick={() => {
                if (day <= 28) void customers.refetch()
              }}
            >
              刷新
            </Button>
          </Space>
        }
      >
        {day <= 28 && customers.isError ? (
          <Alert
            className="mb-4"
            type="error"
            showIcon
            title={getErrorMessage(customers.error, '当日客户加载失败')}
            action={<Button onClick={() => void customers.refetch()}>重试</Button>}
          />
        ) : null}
        <ProTable<Customer, SearchForm>
          toolBarRender={false}
          locale={{ emptyText: '当日暂无计划出账客户' }}
          columns={columns}
          columnsState={{ persistenceKey: 'customer-billing-calendar:table', persistenceType: 'localStorage' }}
          dataSource={day <= 28 ? (customers.data?.data ?? []) : []}
          loading={day <= 28 && customers.isFetching}
          rowKey="id"
          scroll={{ x: '90rem' }}
          formRef={table.formRef}
          form={{ initialValues: table.formInitialValues }}
          search={{ labelWidth: 'auto' }}
          onSubmit={table.onSubmit}
          onReset={table.onReset}
          // Ant Design Table 的固定回调签名包含四个参数。
          // eslint-disable-next-line max-params
          onChange={(_pagination, _filters, sorter, extra) => {
            if (extra.action === 'sort') {
              void navigate({
                search: {
                  ...query,
                  current: 1,
                  sortField: 'createdAt',
                  sortOrder: getCreatedAtSortOrder((Array.isArray(sorter) ? sorter[0] : sorter).order)
                }
              })
              return
            }
          }}
          pagination={{ ...table.pagination, total: day <= 28 ? (customers.data?.total ?? 0) : 0 }}
          options={{
            reload: () => {
              void summary.refetch()
              if (day <= 28) void customers.refetch()
            }
          }}
          size="small"
        />
      </Drawer>
    </PageContainer>
  )
}
